import { usePaginatedQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { FC, useState } from "react";
import { Table, Flex, Heading, Text, Button } from "@radix-ui/themes";
import { StatusBadge } from "./components/status-badge";
import { RelativeTime } from "./components/relative-time";
import { JsonInspector } from "./components/json-inspector";

const STATUSES = ["queued", "processing", "completed", "failed", "ignored"] as const;
const PAGE_SIZE = 50;

export const MembotSignalsPage: FC = () => {
  const [activeStatus, setActiveStatus] = useState<string | undefined>("queued");

  const { results: signals, status, loadMore } = usePaginatedQuery(
    api.atlas.paginateSignals,
    activeStatus ? { status: activeStatus } : {},
    { initialNumItems: PAGE_SIZE }
  );

  return (
    <div>
      <Flex justify="between" align="center" mb="4">
        <Heading size="5">Signals</Heading>
        <Text size="2" color="gray">
          {status === "LoadingFirstPage"
            ? "Loading..."
            : `${signals.length} loaded`}
        </Text>
      </Flex>

      <Flex gap="2" mb="4" wrap="wrap">
        <Button
          variant={activeStatus === undefined ? "solid" : "soft"}
          size="2"
          onClick={() => setActiveStatus(undefined)}
        >
          All
        </Button>
        {STATUSES.map((s) => (
          <Button
            key={s}
            variant={activeStatus === s ? "solid" : "soft"}
            size="2"
            onClick={() => setActiveStatus(s)}
          >
            {s}
          </Button>
        ))}
      </Flex>

      {status === "LoadingFirstPage" ? (
        <Text color="gray">Loading signals...</Text>
      ) : (
        <>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Source Kind</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Session ID</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Dedupe Key</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Payload</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Error</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {signals.map((signal) => (
                <Table.Row key={signal._id}>
                  <Table.Cell>
                    <StatusBadge status={signal.status} />
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" style={{ fontFamily: "monospace" }}>
                      {signal.sourceKind}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" style={{ fontFamily: "monospace" }}>
                      {signal.sessionId.slice(0, 12)}...
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" style={{ fontFamily: "monospace" }}>
                      {signal.dedupeKey.length > 30
                        ? signal.dedupeKey.slice(0, 30) + "..."
                        : signal.dedupeKey}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {signal.payload ? (
                      <JsonInspector data={signal.payload} label="Payload" />
                    ) : (
                      <Text size="1" color="gray">—</Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <RelativeTime timestamp={signal.createdAt} />
                  </Table.Cell>
                  <Table.Cell>
                    {signal.error ? (
                      <Text size="1" color="red">
                        {signal.error.length > 50
                          ? signal.error.slice(0, 50) + "..."
                          : signal.error}
                      </Text>
                    ) : (
                      <Text size="1" color="gray">—</Text>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
              {signals.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={7}>
                    <Text color="gray">
                      No {activeStatus ? `"${activeStatus}" ` : ""}signals found.
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
          {status === "CanLoadMore" && (
            <Flex justify="center" mt="3">
              <Button variant="soft" onClick={() => loadMore(PAGE_SIZE)}>
                Load more
              </Button>
            </Flex>
          )}
        </>
      )}
    </div>
  );
};
